/**
 * Reusable account submission handler for AccountForm dialogs.
 * Handles insert and dialog close logic, and error reporting.
 *
 * @param handleInsert - Function to insert account data
 * @param setDialogOpen - Function to set dialog open state
 * @returns Async function for AccountForm onSubmit
 */

import Papa from 'papaparse';
import { compareSync, hashSync } from 'bcryptjs';
/**
 * Utility helper functions for various operations
 */

/**
 * Helper function to get yesterday's date
 * @returns Yesterday's date as a Date object
 */
export const getYesterday = (): Date => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
};

export const handleAccountSubmit =
    (handleInsert: (data: any) => Promise<any>, setDialogOpen: (open: boolean) => void) => async (data: any) => {
        try {
            await handleInsert(data);
            setDialogOpen(false);
        } catch (error) {
            console.error('Failed to add/modify account:', error);
            // Keep dialog open on error so user can retry
        }
    };

// Helper function to create desk options
export const createDeskOptions = (desks: Array<{ business: string }>) => {
    return desks.map((desk) => ({ value: desk.business, label: desk.business }));
};

// Helper function for number validation rules
export const createNumberRules = (
    required?: string,
    min?: { value: number; message?: string },
    max?: { value: number; message?: string }
) => {
    const rules: any = {};

    if (required) {
        rules.required = required;
    }

    if (min) {
        rules.min = {
            value: min.value,
            message: min.message || `Value must be at least ${min.value}`,
        };
    }

    if (max) {
        rules.max = {
            value: max.value,
            message: max.message || `Value cannot exceed ${max.value}`,
        };
    }

    return rules;
};

// Helper function for text validation rules
export const createTextRules = (
    required?: string,
    minLength?: { value: number; message?: string },
    maxLength?: { value: number; message?: string },
    pattern?: { value: RegExp; message: string }
) => {
    const rules: any = {};

    if (required) {
        rules.required = required;
    }

    if (minLength) {
        rules.minLength = {
            value: minLength.value,
            message: minLength.message || `Minimum length is ${minLength.value} characters`,
        };
    }

    if (maxLength) {
        rules.maxLength = {
            value: maxLength.value,
            message: maxLength.message || `Maximum length is ${maxLength.value} characters`,
        };
    }

    if (pattern) {
        rules.pattern = pattern;
    }

    return rules;
};

// Common validation patterns
export const ValidationPatterns = {
    email: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Please enter a valid email address',
    },
    phone: {
        value: /^[+]?[1-9][\d]{0,15}$/,
        message: 'Please enter a valid phone number',
    },
    alphanumeric: {
        value: /^[a-zA-Z0-9]+$/,
        message: 'Only letters and numbers are allowed',
    },
};

/**
 * Parses CSV text and converts it to an array of transaction objects
 * @param csvText - The raw CSV text content
 * @returns Array of parsed transaction objects
 */
export const parseCSV = (csvText: string): any[] => {
    const result = Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,
    });
    return result.data;
};

/**
 * Checks if two dates are on the same day.
 * @param {Date} date1 - The first date.
 * @param {Date} date2 - The second date.
 * @returns {boolean} True if the dates are on the same day, false otherwise.
 */
export function isSameDay(date1: Date, date2: Date): boolean {
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    );
}

/**
 * Checks if two dates are in the same week.
 * @param {Date} date1 - The first date.
 * @param {Date} date2 - The second date.
 * @returns {boolean} True if the dates are in the same week, false otherwise.
 */
export function isSameWeek(date1: Date, date2: Date): boolean {
    const getStartOfWeek = (date: Date): Date => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday as the start of the week
        d.setHours(0, 0, 0, 0); // Reset time to midnight
        return new Date(d.setDate(diff));
    };

    const startOfWeek1 = getStartOfWeek(date1);
    const startOfWeek2 = getStartOfWeek(date2);

    return startOfWeek1.getTime() === startOfWeek2.getTime();
}

/**
 * Checks if two dates are in the same month.
 * @param {Date} date1 - The first date.
 * @param {Date} date2 - The second date.
 * @returns {boolean} True if the dates are in the same month, false otherwise.
 */
export function isSameMonth(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth();
}

/**
 * Checks if two dates are in the same year.
 * @param {Date} date1 - The first date.
 * @param {Date} date2 - The second date.
 * @returns {boolean} True if the dates are in the same year, false otherwise.
 */
export function isSameYear(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear();
}

// Function to hash a password using bcrypt-compatible hashing.
export function hashPassword(plainPassword: string): string {
    return hashSync(plainPassword, 10);
}

// Function to verify a plaintext password against a hashed password.
export function verifyPassword(plainPassword: string, hashedPassword: string): boolean {
    return compareSync(plainPassword, hashedPassword);
}

export async function translateText(text: string): Promise<string> {
    return text;
}

// Generate a 4-digit random number.
export function generateRandomNumber(): number {
    return 1000 + (Date.now() % 9000);
}

/**
 * Returns the access request message based on user's role and NDA status.
 * - If role is 'pending' (case-insensitive) returns a pending-review message.
 * - If user's `nda` is true returns an instruction to sign the NDA.
 * - Otherwise returns the provided default message.
 */
export function getAccessRequestMessage(user: any, defaultMessage = 'Access request submitted'): string {
    const userRole = user?.role;
    const userNDA = user?.nda;

    if (String(userRole).toLowerCase() === 'pending') {
        return 'Please wait the review of the NDA';
    }

    if (userNDA === true) {
        return 'To get access to objects, please sign the NDA on the top of this page';
    }

    return defaultMessage;
}
